import { useCallback, useEffect } from 'react';
import { registerOverlayPortal, useGetPuck } from '@puckeditor/core';
import { useBlockPropsWriter } from '../puck/blockPropsWriter';

/** Empêche le canvas Puck de désélectionner au relâchement (drag overlay). */
const BUBBLE_PHASE_STOP_EVENTS = ['pointerup', 'mouseup', 'dblclick'];

/**
 * Isole une zone éditable du drag overlay Puck sans bloquer pointerdown vers Tiptap.
 * - Sélectionne le bloc parent au clic (comme InlineTextField Puck)
 * - stopPropagation sur `click` pour éviter le `onClick` canvas qui remet itemSelector à null
 * - `registerOverlayPortal` sur la zone contenu uniquement (pas le bubble menu)
 *
 * @param {string|null} [blockId] - id Puck du composant parent
 */
export function usePuckEditableSurface(containerRef, editor, enabled = true, blockId = null) {
  // Accesseur stable plutôt qu'abonnement : Puck recrée `getSelectorForId` à
  // chaque changement d'état, si bien que `selectBlock` changeait d'identité et
  // que l'effet ci-dessous **réenregistrait ses écouteurs et son portail
  // d'overlay à chaque frappe**, pour chaque zone éditable de la page.
  const getPuck = useGetPuck();
  // Hors du canvas (vue formulaire), le bloc n'est pas dans la portée de Puck :
  // le sélectionner viserait un autre bloc du canvas caché.
  const writer = useBlockPropsWriter();

  const selectBlock = useCallback(() => {
    if (!blockId || writer) return;
    const { dispatch, getSelectorForId } = getPuck();
    const itemSelector = getSelectorForId(blockId);
    if (!itemSelector) return;
    dispatch({ type: 'setUi', ui: { itemSelector } });
  }, [blockId, getPuck, writer]);

  useEffect(() => {
    const container = containerRef?.current;
    if (!enabled || !blockId || !container) return undefined;

    const stopBubble = (e) => {
      e.stopPropagation();
    };

    /** Même pattern que InlineTextField Puck : capture + stop + sélection. */
    const onClickCapture = (e) => {
      e.stopPropagation();
      selectBlock();
    };

    BUBBLE_PHASE_STOP_EVENTS.forEach((type) => {
      container.addEventListener(type, stopBubble, false);
    });
    container.addEventListener('click', onClickCapture, true);

    const cleanupPortal = registerOverlayPortal(container, { disableDragOnFocus: true });

    return () => {
      BUBBLE_PHASE_STOP_EVENTS.forEach((type) => {
        container.removeEventListener(type, stopBubble, false);
      });
      container.removeEventListener('click', onClickCapture, true);
      cleanupPortal?.();
    };
  }, [blockId, containerRef, enabled, selectBlock]);

  useEffect(() => {
    if (!enabled || !blockId || !editor || editor.isDestroyed) return undefined;
    const onFocus = () => selectBlock();
    editor.on('focus', onFocus);
    return () => editor.off('focus', onFocus);
  }, [blockId, editor, enabled, selectBlock]);
}
